import React from 'react';

const AnalyticsChart = ({ data, period, platformData, totalClicks, userTimezone }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <p>No data available for this period</p>
      </div>
    );
  }

  const maxClicks = Math.max(...data.map(d => d.clicks));
  const minClicks = Math.min(...data.map(d => d.clicks));
  const range = maxClicks - minClicks;
  
  // Calculate period total clicks for summary
  const periodTotalClicks = data.reduce((sum, d) => sum + d.clicks, 0);
  const averageClicks = data.length > 0 ? Math.round(periodTotalClicks / data.length) : 0;
  
  // Chart dimensions
  const chartHeight = 240; // Available height for bars (280px container - 40px padding)
  const chartPadding = 16; // Padding from container

  // Platform colors
  const platformColors = {
    web: 'bg-blue-500',
    desktop: 'bg-blue-500',
    mobile: 'bg-green-500',
    android: 'bg-green-500',
    ios: 'bg-purple-500',
    tablet: 'bg-purple-500',
    // Additional platform mappings
    'iOS': 'bg-purple-500',
    'Android': 'bg-green-500',
    'Desktop': 'bg-blue-500',
    'Mobile': 'bg-green-500',
    'Web': 'bg-blue-500'
  };

  // Get platform data for a specific date
  const getPlatformDataForDate = (date) => {
    if (!platformData || !platformData[date]) return null;
    return platformData[date];
  };

  const formatXAxisLabel = (dateString, period) => {
    try {
      // Parse date string as UTC date (YYYY-MM-DD format from backend)
      if (!dateString || typeof dateString !== 'string') {
        return 'Invalid Date';
      }
      
      const parts = dateString.split('-');
      if (parts.length !== 3) {
        return 'Invalid Date';
      }
      
      const [year, month, day] = parts;
      // Create UTC date to avoid timezone issues
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      // Use user timezone if available, otherwise fallback to UTC
      const timezone = userTimezone || 'UTC';
      
      switch (period) {
        case '1d':
          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: timezone });
        case '7d':
        case '30d':
          return date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', timeZone: timezone });
        case '90d':
          return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', timeZone: timezone });
        case '1y':
          return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: timezone });
        default:
          return date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', timeZone: timezone });
      }
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid Date';
    }
  };

  const getBarWidth = () => {
    const totalBars = data.length;
    const availableWidth = 100; // Available width percentage
    const barWidth = Math.max(8, Math.min(20, availableWidth / totalBars));
    return `w-${Math.round(barWidth)}`;
  };

  const getBarHeight = (clicks) => {
    if (maxClicks === 0) return 'h-1';
    const percentage = (clicks / maxClicks) * 100;
    // Ensure minimum height for visibility
    return Math.max(4, Math.round(percentage / 5) * 4);
  };

  return (
    <div className="w-full">
      {/* Chart Container */}
      <div className="relative bg-gray-900 rounded-lg p-4 overflow-hidden" style={{ height: '280px' }}>
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-gray-400">
          <span>{maxClicks}</span>
          <span>{Math.round(maxClicks * 0.75)}</span>
          <span>{Math.round(maxClicks * 0.5)}</span>
          <span>{Math.round(maxClicks * 0.25)}</span>
          <span>0</span>
        </div>

        {/* Chart Area */}
        <div className="ml-12 h-full flex items-end justify-between" style={{ width: 'calc(100% - 3rem)' }}>
          {data.map((item, index) => {
            // Calculate bar height to match Y-axis values exactly
            // Each Y-axis unit should correspond to a specific pixel height
            const pixelsPerUnit = chartHeight / maxClicks;
            const barHeight = Math.max(2, item.clicks * pixelsPerUnit);
            
            // Get platform data for this date
            const datePlatformData = getPlatformDataForDate(item.date);
            
            return (
              <div key={index} className="flex flex-col items-center flex-1">
                {/* Bar */}
                <div className="relative group w-4">
                  {datePlatformData ? (
                    // Multi-platform bar with segments
                    <div className="w-full flex flex-col" style={{ height: `${barHeight}px` }}>
                      {Object.entries(datePlatformData).map(([platform, count]) => {
                        const platformPercentage = (count / item.clicks) * 100;
                        const segmentHeight = (count / item.clicks) * barHeight;
                        const colorClass = platformColors[platform] || 'bg-gray-500';
                        
                        return (
                          <div
                            key={platform}
                            className={`${colorClass} border border-gray-600 transition-all duration-300 hover:opacity-80`}
                            style={{ height: `${segmentHeight}px` }}
                            title={`${platform}: ${count} clicks (${platformPercentage.toFixed(1)}%)`}
                          ></div>
                        );
                      })}
                    </div>
                  ) : (
                    // Single color bar (fallback)
                    <div
                      className="bg-indigo-500 rounded-t transition-all duration-300 hover:bg-indigo-400 w-full"
                      style={{ height: `${barHeight}px` }}
                    ></div>
                  )}
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {formatXAxisLabel(item.date, period)}: {item.clicks} clicks
                    {datePlatformData && (
                      <div className="mt-1">
                        {Object.entries(datePlatformData).map(([platform, count]) => (
                          <div key={platform} className="text-xs">
                            {platform}: {count}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* X-axis labels */}
        <div className="ml-12 mt-1 flex justify-between text-gray-400" style={{ width: 'calc(100% - 3rem)' }}>
          {data.map((item, index) => (
            <span key={index} className="text-center flex-1" style={{ fontSize: '9px', lineHeight: '1', padding: '0 1px' }}>
              {formatXAxisLabel(item.date, period)}
            </span>
          ))}
        </div>
      </div>

      {/* Chart Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 items-end">
        <div className="bg-gray-900 rounded-lg p-3 text-left">
          <div className="text-2xl font-bold text-white">{maxClicks}</div>
          <div className="text-sm text-gray-400">Highest</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-3 text-left">
          <div className="text-2xl font-bold text-white">{averageClicks}</div>
          <div className="text-sm text-gray-400">Average</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-3 text-left">
          <div className="text-2xl font-bold text-white">{totalClicks || periodTotalClicks}</div>
          <div className="text-sm text-gray-400">Total</div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsChart;
